import React, { useEffect, useState } from 'react';
import {
  Activity, Coins, Cpu, Clock3, Users2, AlertTriangle, Bot, Server, TrendingUp,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { toast } from 'sonner';
import { aiAssistantAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { StatCard } from './common/StatCard';
import { CardSkeleton, StatSkeletonGrid, EmptyState } from './common/States';
import { CHART_COLORS, chartColor, tooltipContentStyle, tooltipCursor, axisProps, gridProps } from '../lib/chartTheme';
import { getErrorMessage } from '../lib/utils';

const LICENSE_TONE = {
  active: 'success', trial: 'info', grace: 'warning', expired: 'danger', suspended: 'danger', unlicensed: 'secondary',
};

export default function AIUsageDashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await aiAssistantAPI.getAnalytics();
        setData(res.data);
      } catch (e) {
        toast.error(getErrorMessage(e, 'Failed to load AI analytics'));
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="space-y-4"><StatSkeletonGrid count={6} /><CardSkeleton rows={6} /></div>;
  }
  if (!data) return <EmptyState icon={Activity} title="No AI analytics" description="Usage data will appear once the assistant is used." />;

  const t = data.totals || {};
  const lic = data.license || {};

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-foreground"><Activity className="h-6 w-6 text-primary" /> AI Usage Dashboard</h2>
          <p className="text-sm text-muted-foreground">Requests, cost, agents and providers for {data.month}.</p>
        </div>
        <Badge variant={LICENSE_TONE[lic.status] || 'secondary'} className="capitalize">
          License: {lic.status}{lic.remaining_days ? ` · ${lic.remaining_days}d left` : ''}
        </Badge>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard label="Requests" value={t.requests || 0} icon={Activity} tone="primary" />
        <StatCard label="Tokens" value={t.tokens || 0} icon={Cpu} tone="info" />
        <StatCard label="Est. Cost" value={`$${(t.cost_usd || 0).toFixed(2)}`} icon={Coins} tone="success" animate={false} />
        <StatCard label="Avg Latency" value={t.avg_latency_ms || 0} suffix="ms" icon={Clock3} tone="purple" />
        <StatCard label="Active Users" value={t.active_users || 0} icon={Users2} tone="info" />
        <StatCard label="Failed" value={t.failed || 0} icon={AlertTriangle} tone={t.failed ? 'danger' : 'slate'} />
      </div>

      {data.most_used && (data.most_used.agent || data.most_used.model || data.most_used.provider) && (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-border bg-card p-4"><p className="text-xs text-muted-foreground">Most used agent</p><p className="mt-1 flex items-center gap-2 font-semibold text-foreground"><Bot className="h-4 w-4 text-primary" />{data.most_used.agent || '—'}</p></div>
          <div className="rounded-xl border border-border bg-card p-4"><p className="text-xs text-muted-foreground">Most used model</p><p className="mt-1 flex items-center gap-2 font-semibold text-foreground"><Cpu className="h-4 w-4 text-primary" />{data.most_used.model || '—'}</p></div>
          <div className="rounded-xl border border-border bg-card p-4"><p className="text-xs text-muted-foreground">Most used provider</p><p className="mt-1 flex items-center gap-2 font-semibold text-foreground"><Server className="h-4 w-4 text-primary" />{data.most_used.provider || '—'}</p></div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="border-b border-border/60 pb-3"><CardTitle className="flex items-center gap-2 text-sm font-semibold"><TrendingUp className="h-4 w-4 text-primary" /> Daily Usage</CardTitle></CardHeader>
          <CardContent className="pt-4">
            {(data.timeseries || []).length === 0 ? <EmptyState compact icon={TrendingUp} title="No usage yet" /> : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.timeseries}>
                    <defs><linearGradient id="aiUsage" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.25} /><stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid {...gridProps} />
                    <XAxis dataKey="date" {...axisProps} />
                    <YAxis {...axisProps} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                    <Area type="monotone" dataKey="requests" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#aiUsage)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b border-border/60 pb-3"><CardTitle className="flex items-center gap-2 text-sm font-semibold"><Server className="h-4 w-4 text-primary" /> By Provider</CardTitle></CardHeader>
          <CardContent className="pt-4">
            {(data.by_provider || []).length === 0 ? <EmptyState compact icon={Server} title="No data" /> : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.by_provider} dataKey="requests" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={2} stroke="none">
                      {data.by_provider.map((_, i) => <Cell key={i} fill={chartColor(i)} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipContentStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="border-b border-border/60 pb-3"><CardTitle className="flex items-center gap-2 text-sm font-semibold"><Bot className="h-4 w-4 text-primary" /> By Agent</CardTitle></CardHeader>
          <CardContent className="pt-4">
            {(data.by_agent || []).length === 0 ? <EmptyState compact icon={Bot} title="No agent usage" /> : (
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_agent} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <XAxis type="number" {...axisProps} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={130} {...axisProps} />
                    <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                    <Bar dataKey="requests" radius={[0, 6, 6, 0]} barSize={18}>{data.by_agent.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b border-border/60 pb-3"><CardTitle className="flex items-center gap-2 text-sm font-semibold"><Activity className="h-4 w-4 text-primary" /> Top Prompts</CardTitle></CardHeader>
          <CardContent className="pt-4">
            {(data.top_prompts || []).length === 0 ? <EmptyState compact icon={Activity} title="No prompts yet" /> : (
              <ul className="space-y-2">
                {data.top_prompts.map((p, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{i + 1}</span>
                    <span className="min-w-0 flex-1 truncate text-foreground">{p.prompt}</span>
                    <Badge variant="soft">{p.count}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
